export interface NotificationTargetInput {
  openURL?: string | null;
  targetURL?: string | null;
  target_url?: string | null;
  conversationID?: string | null;
  conversation_id?: string | null;
  chatID?: string | null;
  chat_id?: string | null;
  aroundSequence?: number | string | null;
  around_sequence?: number | string | null;
  sequence?: number | string | null;
  messageID?: string | null;
  message_id?: string | null;
  comment_id?: string | null;
  post_id?: string | null;
  includeNotificationFlag?: boolean;
}

const FALLBACK_BASE_URL = "https://msg.local";
const NOTIFICATION_FLAG_KEY = "notification";
const NOTIFICATION_TRUE_VALUES = new Set(["1", "true", "yes"]);

function trimOrEmpty(value: string | null | undefined): string {
  if (typeof value !== "string") {
    return "";
  }
  return value.trim();
}

function normalizePositiveInt(value: number | string | null | undefined): number | null {
  const parsed =
    typeof value === "number"
      ? value
      : Number.parseInt(typeof value === "string" ? value.trim() : String(value ?? ""), 10);

  if (!Number.isFinite(parsed)) {
    return null;
  }

  const normalized = Math.trunc(parsed);
  if (normalized <= 0) {
    return null;
  }
  return normalized;
}

function parseRelativeURL(value: string): URL | null {
  if (value === "") {
    return null;
  }
  try {
    return new URL(value, FALLBACK_BASE_URL);
  } catch {
    return null;
  }
}

function readChatIDFromPath(pathname: string): string {
  const parts = pathname.split("/").filter((part) => part !== "");
  if (parts.length < 2) {
    return "";
  }

  if (parts[0] === "app" && parts[1] === "chats" && parts.length >= 3) {
    return decodeURIComponent(parts[2] ?? "");
  }
  if (parts[0] === "chats" && parts.length >= 2) {
    return decodeURIComponent(parts[1] ?? "");
  }
  if (parts[0] === "channels" && parts.length >= 2) {
    return decodeURIComponent(parts[1] ?? "");
  }

  return "";
}

export function buildNotificationTargetURL(input: NotificationTargetInput): string {
  const fallbackURL = parseRelativeURL(
    trimOrEmpty(input.targetURL) || trimOrEmpty(input.target_url) || trimOrEmpty(input.openURL),
  );
  const fallbackQuery = fallbackURL?.searchParams ?? new URLSearchParams();

  const explicitConversationID =
    trimOrEmpty(input.conversationID) ||
    trimOrEmpty(input.conversation_id) ||
    trimOrEmpty(input.chatID) ||
    trimOrEmpty(input.chat_id);
  const chatID =
    explicitConversationID !== "" ? explicitConversationID : readChatIDFromPath(fallbackURL?.pathname ?? "");
  if (chatID === "") {
    return "/app";
  }

  const aroundSequence =
    normalizePositiveInt(input.aroundSequence ?? input.around_sequence ?? input.sequence) ??
    normalizePositiveInt(fallbackQuery.get("around_sequence"));
  const explicitMessageID = trimOrEmpty(input.messageID) || trimOrEmpty(input.message_id);
  const messageID =
    explicitMessageID !== ""
      ? explicitMessageID
      : trimOrEmpty(input.comment_id) ||
        trimOrEmpty(input.post_id) ||
        trimOrEmpty(fallbackQuery.get("message_id")) ||
        trimOrEmpty(fallbackQuery.get("comment_id")) ||
        trimOrEmpty(fallbackQuery.get("post_id"));

  const query = new URLSearchParams();
  if (aroundSequence !== null) {
    query.set("around_sequence", String(aroundSequence));
  }
  if (messageID !== "") {
    query.set("message_id", messageID);
  }
  if (input.includeNotificationFlag !== false) {
    query.set(NOTIFICATION_FLAG_KEY, "1");
  }

  const base = `/app/chats/${encodeURIComponent(chatID)}`;
  const encoded = query.toString();
  return encoded === "" ? base : `${base}?${encoded}`;
}

export function isNotificationNavigationSearch(search: string): boolean {
  const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  const value = trimOrEmpty(params.get(NOTIFICATION_FLAG_KEY)).toLowerCase();
  return NOTIFICATION_TRUE_VALUES.has(value);
}
